import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  copyStaffServices,
  getStaffServices,
  listStaff,
  updateStaffServices,
} from "../../api/staffs";
import InfoTooltip from "../common/InfoTooltip";
import ServiceSearch from "../services/ServiceSearch";
import { normalizeForSearch } from "../../utils/normalizeSearch";

const MANAGER_ROLES = ["owner", "manager", "admin", "host"];

const buildDurationOptions = () => {
  const options = [];
  for (let minutes = 15; minutes <= 480; minutes += 15) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    let label = `${minutes} min`;
    if (hours > 0) {
      label = mins
        ? `${hours} hr ${mins} min`
        : `${hours} hr${hours > 1 ? "s" : ""}`;
    }
    options.push({ value: minutes, label });
  }
  return options;
};

const DURATION_OPTIONS = buildDurationOptions();

const coerceNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export default function StaffServicesAssignment({
  staffId,
  salonId,
  token,
  editorRole,
  isManagerOwner,
  onChanged,
  uiClasses = {},
  showInlineHelp = false,
  highlightSectionId = "",
}) {
  const normalizedRole = (editorRole || "").toLowerCase();
  const canEdit =
    typeof isManagerOwner === "boolean"
      ? isManagerOwner
      : MANAGER_ROLES.includes(normalizedRole);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [services, setServices] = useState([]);
  const [draft, setDraft] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [serviceQuery, setServiceQuery] = useState("");
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyList, setCopyList] = useState([]);
  const [copyFromId, setCopyFromId] = useState("");
  const [copyMode, setCopyMode] = useState("missing");
  const [copyStatus, setCopyStatus] = useState("");
  const hasUiClasses = Object.keys(uiClasses || {}).length > 0;
  const modalText = hasUiClasses ? uiClasses.modalText : "text-sm";
  const labelText = hasUiClasses ? uiClasses.labelText : "text-sm text-slate-500";
  const fieldClass = hasUiClasses
    ? uiClasses.fieldClass
    : "rounded border border-slate-300 px-3 py-2 text-sm";
  const btnBase = hasUiClasses ? uiClasses.btnBase : "rounded px-4 py-2 text-sm";
  const cardClass = hasUiClasses
    ? uiClasses.cardClass
    : "rounded-lg border border-slate-200 bg-white p-4 shadow-sm";
  const focusClass = hasUiClasses
    ? "focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
    : "";
  const primaryBtnClass = hasUiClasses
    ? `${btnBase} font-semibold text-white transition bg-slate-900 hover:bg-slate-800 disabled:opacity-60`
    : "rounded bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60";
  const secondaryBtnClass = hasUiClasses
    ? `${btnBase} border border-slate-300 font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50`
    : "rounded border border-slate-300 px-4 py-2 text-sm text-slate-700";
  const highlightedSectionClass =
    highlightSectionId === "section-services"
      ? "ring-2 ring-rose-300 ring-offset-2 transition-shadow duration-200"
      : "";
  const tabTipClass =
    "rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700";

  const loadServices = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getStaffServices(staffId, token);
      const next = Array.isArray(data?.services) ? data.services : [];
      setServices(next);
      setDraft(
        next.map((item) => ({
          ...item,
          override_price: item.override_price ?? null,
          override_duration_minutes: item.override_duration_minutes ?? null,
          shows_online:
            typeof item.shows_online === "boolean" ? item.shows_online : true,
          assigned: !!item.assigned,
        }))
      );
      setExpandedId(null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load services.");
    } finally {
      setLoading(false);
    }
  }, [staffId, token]);

  const loadCopyList = useCallback(async () => {
    if (!salonId || !staffId) return;
    try {
      const list = await listStaff({ salon_id: salonId }, token);
      const filtered = Array.isArray(list)
        ? list.filter((staff) => staff.id !== Number(staffId))
        : [];
      setCopyList(filtered);
    } catch (err) {
      setCopyList([]);
    }
  }, [salonId, staffId, token]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  useEffect(() => {
    loadCopyList();
  }, [loadCopyList]);

  const allSelected = useMemo(
    () => draft.length > 0 && draft.every((item) => item.assigned),
    [draft]
  );

  const searchServices = useMemo(
    () =>
      draft.map((item) => ({
        id: item.service_id,
        name: item.name,
        sku: item.sku,
      })),
    [draft]
  );

  const filteredDraft = useMemo(() => {
    const query = normalizeForSearch(serviceQuery).trim();
    if (!query) return draft;
    const filtered = draft.filter((item) => {
      const nameMatch = normalizeForSearch(item?.name).includes(query);
      const skuMatch = normalizeForSearch(item?.sku).includes(query);
      return nameMatch || skuMatch;
    });
    filtered.sort((a, b) => {
      const aName = normalizeForSearch(a?.name);
      const bName = normalizeForSearch(b?.name);
      const aStarts = aName.startsWith(query);
      const bStarts = bName.startsWith(query);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return aName.localeCompare(bName);
    });
    return filtered;
  }, [draft, serviceQuery]);

  const handleToggleAll = (checked) => {
    if (!canEdit) return;
    setDraft((prev) =>
      prev.map((item) => ({
        ...item,
        assigned: checked,
        shows_online: checked
          ? item.assigned
            ? item.shows_online
            : true
          : item.shows_online,
      }))
    );
  };

  const handleToggleAssigned = (serviceId, checked) => {
    if (!canEdit) return;
    setDraft((prev) =>
      prev.map((item) =>
        item.service_id === serviceId
          ? {
              ...item,
              assigned: checked,
              shows_online: checked ? item.shows_online ?? true : item.shows_online,
            }
          : item
      )
    );
  };

  const updateDraftField = (serviceId, field, value) => {
    if (!canEdit) return;
    setDraft((prev) =>
      prev.map((item) =>
        item.service_id === serviceId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSave = async () => {
    if (!staffId || !canEdit) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        services: draft.map((item) => ({
          service_id: item.service_id,
          assigned: !!item.assigned,
          override_price: item.assigned ? coerceNumber(item.override_price) : null,
          override_duration_minutes: item.assigned
            ? coerceNumber(item.override_duration_minutes)
            : null,
          shows_online: item.assigned
            ? typeof item.shows_online === "boolean"
              ? item.shows_online
              : true
            : null,
        })),
      };
      const data = await updateStaffServices(staffId, payload, token);
      const next = Array.isArray(data?.services) ? data.services : [];
      setServices(next);
      setDraft(
        next.map((item) => ({
          ...item,
          override_price: item.override_price ?? null,
          override_duration_minutes: item.override_duration_minutes ?? null,
          shows_online:
            typeof item.shows_online === "boolean" ? item.shows_online : true,
          assigned: !!item.assigned,
        }))
      );
      onChanged?.(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save assignments.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(
      services.map((item) => ({
        ...item,
        override_price: item.override_price ?? null,
        override_duration_minutes: item.override_duration_minutes ?? null,
        shows_online:
          typeof item.shows_online === "boolean" ? item.shows_online : true,
        assigned: !!item.assigned,
      }))
    );
    setExpandedId(null);
    setError("");
  };

  const handleCopy = async () => {
    if (!staffId || !canEdit || !copyFromId) return;
    setCopyStatus("");
    setError("");
    try {
      const payload = { from_staff_id: Number(copyFromId), mode: copyMode };
      const data = await copyStaffServices(staffId, payload, token);
      const next = Array.isArray(data?.services) ? data.services : [];
      setServices(next);
      setDraft(
        next.map((item) => ({
          ...item,
          override_price: item.override_price ?? null,
          override_duration_minutes: item.override_duration_minutes ?? null,
          shows_online:
            typeof item.shows_online === "boolean" ? item.shows_online : true,
          assigned: !!item.assigned,
        }))
      );
      setCopyStatus("Services copied.");
      onChanged?.(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to copy services.");
    }
  };

  if (loading) {
    return (
      <div className={`${cardClass} ${modalText} text-slate-600`}>
        Loading assigned services...
      </div>
    );
  }

  return (
    <div className={`${cardClass} ${modalText} ${highlightedSectionClass} space-y-4`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900">Assigned Services</h2>
            {showInlineHelp && (
              <InfoTooltip
                text="Choose which services this staff member can perform."
                label="Assigned Services"
                maxWidth={260}
              />
            )}
          </div>
          <p className="text-sm md:text-base text-slate-500">
            Choose the services this staff member can perform.
          </p>
        </div>
        {!canEdit && (
          <span className="text-xs md:text-sm text-slate-500">
            Only managers/owners can edit service assignments.
          </span>
        )}
      </div>
      {showInlineHelp && (
        <div className={tabTipClass}>
          Tip: Keep this list accurate so clients see the right staff during online booking and front desk booking.
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm md:text-base text-rose-700">
          {error}
        </div>
      )}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-md w-full">
          <div className="flex items-center justify-between gap-2">
            <label className={`${labelText} flex items-center gap-2`}>
              Search services...
              {showInlineHelp && (
                <InfoTooltip
                  text="Find a service by name or SKU."
                  label="Search services"
                  maxWidth={240}
                />
              )}
            </label>
            <span className="text-xs md:text-sm text-slate-500">
              Showing {filteredDraft.length} of {draft.length}
            </span>
          </div>
          <div className="mt-2">
            <ServiceSearch
              services={searchServices}
              value={serviceQuery}
              onChange={setServiceQuery}
              onPick={(service) => setServiceQuery(service?.name || "")}
              placeholder="Search services..."
            />
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="text-left text-slate-600">
              <th className="px-3 py-2">
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={!canEdit || draft.length === 0}
                  onChange={(e) => handleToggleAll(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-slate-900"
                />
              </th>
              <th className={`${labelText} uppercase px-3 py-2`}>Service</th>
              <th className={`${labelText} uppercase px-3 py-2`}>Price</th>
              <th className={`${labelText} uppercase px-3 py-2`}>Duration</th>
              <th className={`${labelText} uppercase px-3 py-2`}>
                <span className="flex items-center gap-2">
                  Shows Online
                  {showInlineHelp && (
                    <InfoTooltip
                      text="Controls if clients can book this service with this staff member."
                      label="Shows Online"
                      maxWidth={280}
                    />
                  )}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredDraft.map((service) => {
              const effectivePrice =
                service.override_price ?? service.base_price ?? 0;
              const effectiveDuration =
                service.override_duration_minutes ?? service.duration_minutes;
              const isExpanded = expandedId === service.service_id;
              const toggleExpanded = () => {
                setExpandedId(isExpanded ? null : service.service_id);
              };
              return (
                <Fragment key={service.service_id}>
                  <tr
                    className="border-t hover:bg-slate-50/70"
                    onClick={(event) => {
                      const target = event.target;
                      if (
                        target instanceof HTMLElement &&
                        target.closest("input, select, button")
                      ) {
                        return;
                      }
                      toggleExpanded();
                    }}
                  >
                    <td className="px-3 py-3 align-top">
                      <input
                        type="checkbox"
                        checked={service.assigned}
                        disabled={!canEdit}
                        onChange={(e) =>
                          handleToggleAssigned(service.service_id, e.target.checked)
                        }
                        onClick={(event) => event.stopPropagation()}
                        className="h-4 w-4 rounded border-slate-300 text-slate-900"
                      />
                    </td>
                    <td className="px-3 py-3 align-top">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleExpanded();
                        }}
                        className="text-left text-slate-900 font-medium"
                      >
                        {service.name}
                      </button>
                      {!service.is_active && (
                        <div className="mt-1 text-xs md:text-sm text-rose-500">Inactive</div>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="text-slate-700">${effectivePrice}</div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="text-slate-700">
                        {effectiveDuration ? `${effectiveDuration} min` : "--"}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="text-slate-700">
                        {service.assigned
                          ? service.shows_online
                            ? "Yes"
                            : "No"
                          : "--"}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="border-t bg-slate-50/70">
                      <td className="px-3 py-3" colSpan={5}>
                        <div className="grid gap-3 sm:grid-cols-3">
                          <label className="text-xs md:text-sm text-slate-500">
                            <span className={`${labelText} uppercase flex items-center gap-2`}>
                              Override Price
                              {showInlineHelp && (
                                <InfoTooltip
                                  text="Use a different price for this staff member."
                                  label="Override Price"
                                  maxWidth={260}
                                />
                              )}
                            </span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              disabled={!canEdit || !service.assigned}
                              value={service.override_price ?? ""}
                              placeholder={
                                service.base_price !== null
                                  ? `${service.base_price}`
                                  : "Use base"
                              }
                              onChange={(e) =>
                                updateDraftField(
                                  service.service_id,
                                  "override_price",
                                  e.target.value === "" ? null : e.target.value
                                )
                              }
                              className={`mt-1 w-full ${fieldClass} ${focusClass}`.trim()}
                            />
                          </label>
                          <label className="text-xs md:text-sm text-slate-500">
                            <span className={`${labelText} uppercase flex items-center gap-2`}>
                              Override Duration
                              {showInlineHelp && (
                                <InfoTooltip
                                  text="Use a different time for this staff member."
                                  label="Override Duration"
                                  maxWidth={260}
                                />
                              )}
                            </span>
                            <select
                              disabled={!canEdit || !service.assigned}
                              value={service.override_duration_minutes ?? ""}
                              onChange={(e) =>
                                updateDraftField(
                                  service.service_id,
                                  "override_duration_minutes",
                                  e.target.value === ""
                                    ? null
                                    : Number(e.target.value)
                                )
                              }
                              className={`mt-1 w-full ${fieldClass} ${focusClass}`.trim()}
                            >
                              <option value="">
                                {service.duration_minutes
                                  ? `Use base (${service.duration_minutes} min)`
                                  : "Use base"}
                              </option>
                              {DURATION_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label className="text-xs md:text-sm text-slate-500">
                            <span className={`${labelText} uppercase flex items-center gap-2`}>
                              Shows Online
                              {showInlineHelp && (
                                <InfoTooltip
                                  text="Choose if clients can book it online."
                                  label="Shows Online"
                                  maxWidth={240}
                                />
                              )}
                            </span>
                            <select
                              disabled={!canEdit || !service.assigned}
                              value={service.shows_online ? "yes" : "no"}
                              onChange={(e) =>
                                updateDraftField(
                                  service.service_id,
                                  "shows_online",
                                  e.target.value === "yes"
                                )
                              }
                              className={`mt-1 w-full ${fieldClass} ${focusClass}`.trim()}
                            >
                              <option value="yes">Yes</option>
                              <option value="no">No</option>
                            </select>
                          </label>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {!filteredDraft.length && (
              <tr>
                <td
                  className="px-3 py-6 text-slate-500 text-center"
                  colSpan={5}
                >
                  No services match your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      <div className="flex flex-wrap items-center gap-2 justify-end">
        <button
          type="button"
          onClick={handleCancel}
          className={secondaryBtnClass}
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!canEdit || saving}
          onClick={handleSave}
          className={primaryBtnClass}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-slate-50">
        <button
          type="button"
          className="w-full px-4 py-3 text-left text-sm md:text-base font-semibold text-slate-700 flex items-center justify-between"
          onClick={() => setCopyOpen((prev) => !prev)}
        >
          <span className="flex items-center gap-2">
            Copy from another employee
            {showInlineHelp && (
              <InfoTooltip
                text="Reuse another staff member's service list."
                label="Copy from another employee"
                maxWidth={260}
              />
            )}
          </span>
          <span className="text-xs md:text-sm text-slate-500">
            {copyOpen ? "Hide" : "Show"}
          </span>
        </button>
        {copyOpen && (
          <div className="border-t border-slate-200 px-4 py-4 space-y-3">
            <label className="text-sm md:text-base text-slate-700">
              <span className={`${labelText} uppercase flex items-center gap-2`}>
                Select employee
                {showInlineHelp && (
                  <InfoTooltip
                    text="Choose the staff member to copy from."
                    label="Select employee"
                    maxWidth={240}
                  />
                )}
              </span>
              <select
                value={copyFromId}
                onChange={(e) => setCopyFromId(e.target.value)}
                className={`w-full ${fieldClass} ${focusClass}`.trim()}
                disabled={!canEdit}
              >
                <option value="">Choose staff member</option>
                {copyList.map((staff) => (
                  <option key={staff.id} value={staff.id}>
                    {staff.display_name}
                  </option>
                ))}
              </select>
            </label>

            <div className="space-y-2 text-sm md:text-base text-slate-700">
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="copy_mode"
                  value="missing"
                  checked={copyMode === "missing"}
                  onChange={() => setCopyMode("missing")}
                  disabled={!canEdit}
                  className="mt-1 h-4 w-4 rounded-full border border-slate-300 text-slate-900 focus:ring-0"
                />
                <span>
                  Copy only services which haven't already been assigned to this
                  staff member.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="copy_mode"
                  value="merge_update"
                  checked={copyMode === "merge_update"}
                  onChange={() => setCopyMode("merge_update")}
                  disabled={!canEdit}
                  className="mt-1 h-4 w-4 rounded-full border border-slate-300 text-slate-900 focus:ring-0"
                />
                <span>
                  Copy services which haven't already been assigned, and also
                  update the details for services already assigned.
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="radio"
                  name="copy_mode"
                  value="replace_all"
                  checked={copyMode === "replace_all"}
                  onChange={() => setCopyMode("replace_all")}
                  disabled={!canEdit}
                  className="mt-1 h-4 w-4 rounded-full border border-slate-300 text-slate-900 focus:ring-0"
                />
                <span>
                  Remove all existing services and replace them with the
                  services from the selected employee.
                </span>
              </label>
            </div>

            <div className="flex items-center justify-between">
              {copyStatus && (
                <span className="text-sm md:text-base text-emerald-600">{copyStatus}</span>
              )}
              <button
                type="button"
                onClick={handleCopy}
                disabled={!canEdit || !copyFromId}
                className={primaryBtnClass}
              >
                Copy Services
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
